import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Minus, X } from "lucide-react";

/**
 * NYSE-style scrolling ticker tape.
 *
 * - Fetches active items from /api/ticker/items on mount + every 45s.
 * - Renders an infinite marquee (content duplicated twice for seamless scroll).
 * - Each item shows: LABEL · value  ·  change (colored)
 * - The whole ribbon is dismissable per-tab via sessionStorage.
 * - Renders nothing when there are zero items.
 */
export default function TickerTape() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("cq_ticker_hidden") === "1"; } catch { return false; }
  });
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/ticker/items");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch { /* silent — ticker is decorative */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    timerRef.current = setInterval(load, 45000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line
  }, [user?.id]);

  // Refresh instantly when admin saves changes elsewhere in the app.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("cq:ticker:refresh", h);
    return () => window.removeEventListener("cq:ticker:refresh", h);
    // eslint-disable-next-line
  }, [user?.id]);

  const doubled = useMemo(() => [...items, ...items], [items]);

  if (!user || dismissed || items.length === 0) return null;

  return (
    <div
      className="cq-tape relative w-full overflow-hidden select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="ticker-tape"
      role="marquee"
      aria-label="Live announcements"
    >
      {/* NYSE-style black backdrop with subtle scanlines */}
      <div className="absolute inset-0 bg-[#0a0a0a]" />
      <div className="absolute inset-0 pointer-events-none cq-tape-scanlines" />
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />

      {/* Dismiss */}
      <button
        type="button"
        onClick={() => {
          try { sessionStorage.setItem("cq_ticker_hidden", "1"); } catch { /* no-op */ }
          setDismissed(true);
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 z-20 text-slate-400 hover:text-white p-1 rounded"
        title="Hide ticker for this session"
        aria-label="Hide ticker"
        data-testid="ticker-hide"
      >
        <X size={12} />
      </button>

      {/* LIVE badge — pulsing red bug like a real ticker */}
      <span className="absolute left-2 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-sm bg-red-600/95 text-white text-[9px] font-black uppercase tracking-widest shadow-md">
        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
        Live
      </span>

      <div
        className={`cq-tape-track flex items-center gap-10 py-1.5 pl-16 pr-8 whitespace-nowrap ${paused ? "cq-tape-paused" : ""}`}
        style={{ "--tape-duration": `${Math.max(24, items.length * 6)}s` }}
      >
        {doubled.map((it, idx) => (
          <TickerItem key={`${it.id}-${idx}`} item={it} />
        ))}
      </div>

      <style>{`
        .cq-tape {
          border-bottom: 1px solid #1f2937;
          font-family: 'JetBrains Mono', 'Menlo', 'Consolas', ui-monospace, monospace;
          font-size: 12px;
          color: #e5e7eb;
        }
        .cq-tape-scanlines {
          background: repeating-linear-gradient(
            180deg,
            rgba(255,255,255,0.02) 0px,
            rgba(255,255,255,0.02) 1px,
            transparent 1px,
            transparent 3px
          );
          opacity: 0.7;
        }
        .cq-tape-track {
          animation-name: cq-tape-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          animation-duration: var(--tape-duration, 60s);
          will-change: transform;
        }
        /* Mobile: shorter content in view — speed the scroll up so it doesn't feel frozen */
        @media (max-width: 768px) {
          .cq-tape-track { animation-duration: calc(var(--tape-duration, 60s) * 0.12); }
        }
        @media (max-width: 480px) {
          .cq-tape-track { animation-duration: calc(var(--tape-duration, 60s) * 0.09); }
        }
        .cq-tape-paused { animation-play-state: paused; }
        @keyframes cq-tape-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function TickerItem({ item }) {
  const color = item.color || "#22c55e";
  const change = (item.change || "").trim();
  const dir = getDir(change);
  const Arrow = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;

  return (
    <span
      className="inline-flex items-baseline gap-2 text-[13px] tabular-nums"
      data-testid={`ticker-item-${item.id}`}
    >
      <span
        className="font-black tracking-widest text-[12px]"
        style={{ color }}
      >
        {item.label?.toUpperCase()}
      </span>
      {item.value ? (
        <span className="text-slate-200 font-semibold">{item.value}</span>
      ) : null}
      {change ? (
        <span
          className="inline-flex items-center gap-1 font-black"
          style={{ color: dirColor(dir, color) }}
        >
          <Arrow size={12} strokeWidth={3} />
          {change}
        </span>
      ) : null}
      <span className="text-slate-600 ml-2 select-none">•</span>
    </span>
  );
}

function getDir(change) {
  if (!change) return "flat";
  const first = change.trim()[0];
  if (first === "+") return "up";
  if (first === "-" || first === "−") return "down";
  return "flat";
}

function dirColor(dir, fallback) {
  if (dir === "up") return "#22c55e";
  if (dir === "down") return "#ef4444";
  return fallback || "#e5e7eb";
}
